import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import RoboWidget from "@/components/RoboWidget";
import AnnouncementBar from "@/components/AnnouncementBar";
import SkipToContent from "@/components/SkipToContent";
import RoutePrefetcher from "@/components/RoutePrefetcher";
import { useAuth } from "@/auth/AuthContext";

const NAV = [
  { to: "/modules", label: "Modules" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/works-with-epic", label: "Works with Epic →", accent: true },
  { to: "/architecture", label: "Architecture" },
  { to: "/outcomes", label: "Outcomes" },
  {
    label: "Compare",
    children: [
      { to: "/compare-all",          label: "All systems →", isNew: true },
      { to: "/compare",              label: "vs. Epic →" },
      { to: "/compare-vs-athena",    label: "vs. Athena →" },
      { to: "/compare-vs-cerner",    label: "vs. Cerner →" },
    ],
  },
  { to: "/pricing", label: "Pricing" },
  { to: "/hipaa", label: "Security" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
];

function NavDropdown({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="nav-dropdown-compare"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-sm font-medium text-[#0A140F] hover:text-[#8B2B3A] transition-colors flex items-center gap-1 whitespace-nowrap"
        data-testid="nav-dropdown-compare-trigger"
        aria-expanded={open}
      >
        {item.label}
        <svg width="10" height="10" viewBox="0 0 10 10" className={`transition-transform ${open ? "rotate-180" : ""}`} aria-hidden>
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="square" />
        </svg>
      </button>
      {open && (
        <div className="absolute left-0 top-full pt-2 z-40">
          <div className="hard-border bg-white min-w-[180px] py-1" data-testid="nav-dropdown-compare-menu">
            {item.children.map((c) => (
              <NavLink
                key={c.to}
                to={c.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-dropdown-link-${c.to.split("/").pop()}`}
                className={({ isActive }) =>
                  `flex items-center justify-between gap-2 px-4 py-2.5 text-sm whitespace-nowrap transition-colors ${
                    isActive ? "text-[#8B2B3A] bg-[#F8F7F4]" : "text-[#0A140F] hover:bg-[#F8F7F4] hover:text-[#8B2B3A]"
                  }`
                }
              >
                <span>{c.label}</span>
                {c.isNew && (
                  <span
                    className="font-mono text-[9px] tracking-[0.18em] uppercase bg-[#0E8B7E] text-[#F8F7F4] px-1.5 py-0.5"
                    data-testid="nav-dropdown-new-badge"
                  >
                    New
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TopBanner() {
  return (
    <div className="bg-[#0A140F] text-[#F8F7F4] text-[11px] font-mono tracking-[0.18em] uppercase" data-testid="topbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="ticker-dot inline-block" aria-hidden />
          <span className="hidden sm:inline">HIPAA Compliant · BAA Available</span>
          <span className="sm:hidden">HIPAA Compliant · BAA Available</span>
        </div>
        <div className="hidden md:flex items-center gap-6">
          <span>v0.1 · Early Access</span>
          <Link to="/demo" className="hover:text-[#8B2B3A] transition-colors" data-testid="top-banner-demo-link">Live demo →</Link>
        </div>
      </div>
    </div>
  );
}

function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { isAuthenticated, user } = useAuth();

  useEffect(() => {
    const f = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", f);
    f();
    return () => window.removeEventListener("scroll", f);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="marketing-header"
      className={`sticky top-0 z-40 border-b border-[#D6D2C4] ${scrolled ? "backdrop-blur-xl bg-[#F8F7F4]/85" : "bg-[#F8F7F4]"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-6">
        <Logo testId="marketing-header-logo" />
        <nav className="hidden lg:flex items-center gap-5 xl:gap-7" aria-label="Primary">
          {NAV.map((n) =>
            n.children ? <NavDropdown key={n.label} item={n} /> : (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors flex items-center gap-1.5 whitespace-nowrap ${
                    isActive ? "text-[#8B2B3A]" : n.accent ? "text-[#0E8B7E] hover:text-[#0A140F]" : "text-[#0A140F] hover:text-[#8B2B3A]"
                  }`
                }
              >
                {n.accent && (
                  <span className="relative flex w-2 h-2" aria-hidden>
                    <span className="absolute inset-0 rounded-full bg-[#0E8B7E] opacity-75 animate-ping" />
                    <span className="relative w-2 h-2 rounded-full bg-[#0E8B7E]" />
                  </span>
                )}
                {n.label}
              </NavLink>
            )
          )}
        </nav>
        <div className="hidden md:flex items-center gap-3 shrink-0">
          {isAuthenticated ? (
            <Link
              to="/dashboard"
              className="text-sm font-medium text-[#0A140F] hover:text-[#8B2B3A] transition-colors flex items-center gap-2 whitespace-nowrap"
              data-testid="nav-dashboard-link"
            >
              <span className="w-7 h-7 bg-[#1A2E27] text-[#F8F7F4] flex items-center justify-center font-mono text-[11px]">
                {(user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
              </span>
              <span>Dashboard</span>
            </Link>
          ) : (
            <Link
              to="/signin"
              className="text-sm font-medium text-[#0A140F] hover:text-[#8B2B3A] transition-colors whitespace-nowrap"
              data-testid="nav-signin-link"
            >
              Sign in
            </Link>
          )}
          <Link
            to={isAuthenticated ? "/contact" : "/signup"}
            className="bg-[#8B2B3A] text-[#F8F7F4] px-5 py-2.5 text-sm font-medium hover:bg-[#5A1C26] transition-colors whitespace-nowrap"
            data-testid="nav-cta-primary"
          >
            {isAuthenticated ? "Request demo" : "Sign up →"}
          </Link>
        </div>
        <button
          className="lg:hidden w-10 h-10 flex items-center justify-center border border-[#D6D2C4]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="marketing-mobile-toggle"
        >
          <span className="font-mono text-xs">{open ? "CLOSE" : "MENU"}</span>
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#D6D2C4] bg-[#F8F7F4]">
          <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3">
            {NAV.map((n) =>
              n.children ? (
                <div key={n.label} className="py-1">
                  <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-[#4A5A52]">{n.label}</div>
                  <div className="mt-1 ml-2 flex flex-col">
                    {n.children.map((c) => (
                      <NavLink
                        key={c.to}
                        to={c.to}
                        className="text-sm font-medium py-2"
                        data-testid={`mobile-nav-${c.to.replace(/\//g, "-")}`}
                      >
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={`text-sm font-medium py-2 flex items-center gap-2 ${n.accent ? "text-[#0E8B7E]" : ""}`}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                >
                  {n.accent && <span className="w-2 h-2 rounded-full bg-[#0E8B7E]" aria-hidden />}
                  {n.label}
                </NavLink>
              )
            )}
            <Link to="/dashboard" className="text-sm font-medium py-2" data-testid="mobile-nav-dashboard">Dashboard</Link>
            <Link to="/contact" className="bg-[#8B2B3A] text-[#F8F7F4] px-4 py-3 text-sm font-medium text-center" data-testid="mobile-nav-cta">Request Demo</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#0A140F] text-[#F8F7F4]" data-testid="marketing-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-5 gap-10">
        <div className="col-span-2 md:col-span-2">
          <Logo inverse testId="footer-logo" />
          <p className="mt-6 text-sm text-[#8A9992] leading-relaxed max-w-sm">
            The operating system for modern healthcare. Six AI modules, one HIPAA-compliant runtime, zero new screens for your clinicians.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {["HIPAA Compliant", "BAA Available", "AWS HIPAA-Eligible"].map((b) => (
              <span key={b} className="font-mono text-[10px] tracking-[0.2em] uppercase border border-[#2A3E37] px-2.5 py-1 text-[#8A9992]">
                {b}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="mono-label text-[#8A9992] mb-4">Product</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/modules" className="hover:text-[#8B2B3A]" data-testid="footer-link-modules">Modules</Link></li>
            <li><Link to="/architecture" className="hover:text-[#8B2B3A]" data-testid="footer-link-architecture">Architecture</Link></li>
            <li><Link to="/how-it-works" className="hover:text-[#8B2B3A]" data-testid="footer-link-hiw">How it works</Link></li>
            <li><Link to="/how-it-fits" className="hover:text-[#8B2B3A]" data-testid="footer-link-how-it-fits">How it fits your EHR</Link></li>
            <li><Link to="/denials" className="hover:text-[#8B2B3A]" data-testid="footer-link-denials">Payer denial playbooks</Link></li>
            <li><Link to="/pricing" className="hover:text-[#8B2B3A]" data-testid="footer-link-pricing">Pricing</Link></li>
            <li><Link to="/compare-all" className="hover:text-[#8B2B3A]" data-testid="footer-link-compare-all">CareSenta vs. all systems</Link></li>
            <li><Link to="/compare" className="hover:text-[#8B2B3A]" data-testid="footer-link-compare-epic">CareSenta vs. Epic</Link></li>
            <li><Link to="/compare-vs-athena" className="hover:text-[#8B2B3A]" data-testid="footer-link-compare-athena">CareSenta vs. Athena</Link></li>
            <li><Link to="/compare-vs-cerner" className="hover:text-[#8B2B3A]" data-testid="footer-link-compare-cerner">CareSenta vs. Cerner</Link></li>
            <li><Link to="/demo" className="hover:text-[#8B2B3A]" data-testid="footer-link-demo">Live demo</Link></li>
            <li><Link to="/try/refills" className="hover:text-[#8B2B3A]" data-testid="footer-link-try-refills">Try one AI decision (free)</Link></li>
            <li><Link to="/dashboard" className="hover:text-[#8B2B3A]" data-testid="footer-link-dashboard">Dashboard</Link></li>
          </ul>
        </div>
        <div>
          <div className="mono-label text-[#8A9992] mb-4">Company</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/about" className="hover:text-[#8B2B3A]" data-testid="footer-link-about">About</Link></li>
            <li><Link to="/customers" className="hover:text-[#8B2B3A]" data-testid="footer-link-customers">Customers</Link></li>
            <li><Link to="/blog" className="hover:text-[#8B2B3A]" data-testid="footer-link-blog">Blog</Link></li>
            <li><Link to="/whats-new" className="hover:text-[#8B2B3A]" data-testid="footer-link-whats-new">What's new</Link></li>
            <li><Link to="/faq" className="hover:text-[#8B2B3A]" data-testid="footer-link-faq">FAQ</Link></li>
            <li><Link to="/contact" className="hover:text-[#8B2B3A]" data-testid="footer-link-contact">Contact</Link></li>
          </ul>
        </div>
        <div>
          <div className="mono-label text-[#8A9992] mb-4">Trust</div>
          <ul className="space-y-3 text-sm">
            <li><Link to="/hipaa" className="hover:text-[#8B2B3A]" data-testid="footer-link-hipaa">HIPAA / BAA</Link></li>
            <li><Link to="/hipaa#trust-center" className="hover:text-[#8B2B3A]" data-testid="footer-link-trust">Trust Center</Link></li>
            <li><Link to="/validation" className="hover:text-[#8B2B3A]" data-testid="footer-link-validation">Validation</Link></li>
            <li><Link to="/outcomes" className="hover:text-[#8B2B3A]" data-testid="footer-link-outcomes">Outcomes</Link></li>
            <li><Link to="/terms" className="hover:text-[#8B2B3A]" data-testid="footer-link-terms">Terms</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-[#2A3E37]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row justify-between gap-3 font-mono text-[11px] tracking-[0.18em] uppercase text-[#8A9992]">
          <div>© {new Date().getFullYear()} CareSenta, Inc. · Built for HIPAA-regulated entities.</div>
          <div>Made with clinical discipline · Boston · Remote</div>
        </div>
      </div>
    </footer>
  );
}

export default function MarketingLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-[#F8F7F4]" data-testid="marketing-layout">
      <SkipToContent />
      <RoutePrefetcher />
      <AnnouncementBar />
      <TopBanner />
      <Header />
      <main id="main-content" tabIndex={-1} className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <RoboWidget />
    </div>
  );
}
